import React from 'react';
import '../styles/Privacy.css';

const SECTIONS = [
  {
    id: 'collect',
    title: '1. Information We Collect',
    content: (
      <>
        <p><strong>Information you provide</strong></p>
        <ul>
          <li>Account information: name, email address, or other details you provide when creating a profile.</li>
          <li>Resume and interview data: any resume content, answers, or practice-session data you upload or generate within the App.</li>
        </ul>
        <p><strong>Information collected automatically</strong></p>
        <ul>
          <li>Device information: device type, operating system, and app version.</li>
          <li>Usage data: features used, session duration, and interaction patterns within the App.</li>
          <li>Push notification tokens: a unique device identifier used to deliver reminders and updates via services like Expo Notifications.</li>
        </ul>
        <p><strong>Information we do not collect</strong></p>
        <p>
          We do not knowingly collect sensitive personal information such as
          government IDs, financial account details, or precise location,
          unless explicitly required for a specific feature and disclosed at
          the time of collection.
        </p>
      </>
    ),
  },
  {
    id: 'use',
    title: '2. How We Use Your Information',
    content: (
      <ul>
        <li>Provide and personalize core features (mock interviews, aptitude challenges, resume tools, progress tracking).</li>
        <li>Send scheduled push notifications and reminders (e.g., daily prep tips based on the day of the week).</li>
        <li>Improve app performance, fix bugs, and develop new features.</li>
        <li>Communicate updates, support responses, or policy changes.</li>
        <li>Maintain security and prevent misuse of the App.</li>
      </ul>
    ),
  },
  {
    id: 'notifications',
    title: '3. Push Notifications',
    content: (
      <p>
        CodeMatrix uses push notifications to send reminders such as daily
        practice prompts. You can opt out at any time by disabling
        notification permissions in your device settings. Disabling
        notifications will not affect your ability to use the rest of the
        App.
      </p>
    ),
  },
  {
    id: 'sharing',
    title: '4. Data Sharing and Disclosure',
    content: (
      <>
        <p>We do not sell your personal information. We may share information with:</p>
        <ul>
          <li>Service providers who help operate the App (e.g., cloud hosting, notification delivery, analytics), bound by confidentiality obligations.</li>
          <li>Legal authorities, if required by law, regulation, or valid legal process.</li>
          <li>Business transfers, in the event of a merger, acquisition, or sale of assets, where user information may be transferred as part of that transaction.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'security',
    title: '5. Data Storage and Security',
    content: (
      <p>
        We take reasonable technical and organizational measures to protect
        your information from unauthorized access, alteration, disclosure, or
        destruction. However, no method of electronic storage or transmission
        is 100% secure, and we cannot guarantee absolute security.
      </p>
    ),
  },
  {
    id: 'retention',
    title: '6. Data Retention',
    content: (
      <p>
        We retain your information for as long as your account is active or
        as needed to provide the App's services. You may request deletion of
        your data at any time (see Section 8).
      </p>
    ),
  },
  {
    id: 'children',
    title: "7. Children's Privacy",
    content: (
      <p>
        CodeMatrix is not directed at children under the age of 16 (or the
        applicable minimum age in your jurisdiction). We do not knowingly
        collect personal information from children. If you believe a child
        has provided us with personal information, please contact us so we
        can delete it.
      </p>
    ),
  },
  {
    id: 'rights',
    title: '8. Your Rights and Choices',
    content: (
      <>
        <p>Depending on your location, you may have the right to:</p>
        <ul>
          <li>Access, correct, or delete your personal information.</li>
          <li>Withdraw consent for data processing.</li>
          <li>Opt out of push notifications or marketing communications.</li>
          <li>Request a copy of your data.</li>
        </ul>
        <p>To exercise these rights, contact us using the details in Section 10.</p>
      </>
    ),
  },
  {
    id: 'changes',
    title: '9. Changes to This Policy',
    content: (
      <p>
        We may update this Privacy Policy from time to time. Changes will be
        posted on this page with an updated "Last updated" date. Continued
        use of the App after changes constitutes acceptance of the revised
        policy.
      </p>
    ),
  },
  {
    id: 'contact',
    title: '10. Contact Us',
    content: (
      <>
        <p>
          If you have questions or concerns about this Privacy Policy or your
          data, please contact us at:
        </p>
        <p>
          <a href="mailto:predictbyte@gmail.com">predictbyte@gmail.com</a>
        </p>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="privacy-page">


      <main className="privacy-main">
        <h1>Privacy Policy</h1>
        <p className="privacy-updated">Last updated: August 21, 2026</p>

        <p>
          CodeMatrix ("we," "our," or "us") respects your privacy and is
          committed to protecting it through this Privacy Policy. This
          document explains what information we collect, how we use it, and
          your rights regarding that information when you use our website
          and mobile application (together, the "App").
        </p>
        <p>By using CodeMatrix, you agree to the terms of this Privacy Policy.</p>

        <nav className="privacy-toc">
          <h2>Contents</h2>
          <ol>
            {SECTIONS.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>{section.title}</a>
              </li>
            ))}
          </ol>
        </nav>

        {SECTIONS.map((section) => (
          <section key={section.id} id={section.id}>
            <h2>{section.title}</h2>
            {section.content}
          </section>
        ))}

      
      </main>

      <footer className="privacy-footer">
        &copy; 2026 CodeMatrix. All rights reserved.
      </footer>
    </div>
  );
}
